/**
 * Unified component for adding repos and restoring access
 * Story 382: Unified repo access flow with auto-recheck on tab return
 *
 * Story 585: add mode opens on the repos Ceetrix can see, leads with installing
 * the GitHub App when it can see none, and keeps the URL box behind a link
 *
 * Handles two entry points:
 * - mode="add": User picks a listed repo (or enters a URL), checks access, guides to GitHub if needed
 * - mode="restore": Skip URL input, show guidance immediately for inaccessible project
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { config } from '../../config';
import { projectKeys } from '../../hooks/useProjects';
import { useGitHubRepos } from '../../hooks/useGitHubRepos';
import type { AddRepoByUrlResponse } from '../../hooks/useAddProjectByUrl';
import { RepoPicker } from './RepoPicker';
import './RepoAccessResolver.css';

/** State machine states */
type ResolverState = 'idle' | 'checking' | 'accessible' | 'needs_app' | 'error';

/** How the user is choosing a repo in add mode (Story 585) */
type EntryMode = 'list' | 'address';

/** Error types for different failure scenarios */
type ErrorType = 'invalid_url' | 'network' | 'github_down' | 'auth_expired' | null;

/** Project info returned on success */
interface ProjectInfo {
  id: string;
  slug: string;
  created: boolean;
}

interface RepoAccessResolverProps {
  /** Mode determines initial state and UI */
  mode: 'add' | 'restore';
  /** Required for restore mode - the repo slug to restore */
  repoSlug?: string;
  /** Called when access is confirmed and project is ready */
  onSuccess: (project: ProjectInfo) => void;
  /** Called when user closes the resolver */
  onClose?: () => void;
}

/** Parse various GitHub URL formats to owner/repo */
function parseRepoUrl(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  // Already in owner/repo format
  if (/^[a-zA-Z0-9_.-]+\/[a-zA-Z0-9_.-]+$/.test(trimmed)) {
    return trimmed;
  }

  // URL formats
  const patterns = [
    // https://github.com/owner/repo or github.com/owner/repo
    /^(?:https?:\/\/)?github\.com\/([a-zA-Z0-9_.-]+\/[a-zA-Z0-9_.-]+)/,
    // git@github.com:owner/repo.git
    /^git@github\.com:([a-zA-Z0-9_.-]+\/[a-zA-Z0-9_.-]+)(?:\.git)?$/,
  ];

  for (const pattern of patterns) {
    const match = trimmed.match(pattern);
    if (match) {
      // Strip .git suffix if present
      return match[1].replace(/\.git$/, '');
    }
  }

  return null;
}

/** GitHub App slug from environment or default */
const GITHUB_APP_SLUG = 'ceetrix';

/** Build install URL for GitHub App */
function buildInstallUrl(repoSlug: string, returnUri: string): string {
  const [owner] = repoSlug.split('/');
  const baseUrl = `https://github.com/apps/${GITHUB_APP_SLUG}/installations/new`;
  const params = new URLSearchParams({
    suggested_target_id: owner,
    return_uri: returnUri,
  });
  return `${baseUrl}?${params}`;
}

export function RepoAccessResolver({
  mode,
  repoSlug: initialRepoSlug,
  onSuccess,
  onClose,
}: RepoAccessResolverProps) {
  const queryClient = useQueryClient();

  // State machine
  const [state, setState] = useState<ResolverState>(mode === 'restore' ? 'checking' : 'idle');
  const [repoSlug, setRepoSlug] = useState<string | null>(initialRepoSlug ?? null);
  const [urlInput, setUrlInput] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorType, setErrorType] = useState<ErrorType>(null);
  const [installUrl, setInstallUrl] = useState<string | null>(null);
  const [waitingForReturn, setWaitingForReturn] = useState(false);
  const [project, setProject] = useState<ProjectInfo | null>(null);

  // Story 585: repos the Ceetrix App can see, loaded only in add mode
  const repoList = useGitHubRepos(mode === 'add');
  const { refetch: refetchRepos } = repoList;
  const [entry, setEntry] = useState<EntryMode>('list');
  // Waiting for the user to come back from the App install page (not the needs_app recheck)
  const [waitingForInstall, setWaitingForInstall] = useState(false);

  // Track if we've started the initial check in restore mode
  const initialCheckDone = useRef(false);

  // Mutation for checking/adding repo access
  const checkAccess = useMutation({
    mutationFn: async (slug: string): Promise<AddRepoByUrlResponse> => {
      const response = await fetch(`${config.apiUrl}/api/v1/repos/add`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ repo_url: slug }),
      });

      if (!response.ok) {
        // 5xx: server is down, no point parsing body
        if (response.status >= 500) {
          throw { type: 'github_down' as const, message: 'GitHub is temporarily unavailable.' };
        }
        // 4xx: body contains structured status (needs_installation, no_access, auth_failed)
        // Parse it so onSuccess can route to the correct UI state
        try {
          return await response.json();
        } catch {
          throw { type: 'network' as const, message: 'Network error. Check your connection.' };
        }
      }

      return response.json();
    },
    onSuccess: (data) => {
      if (data.status === 'created' || data.status === 'existing') {
        // Success - project accessible
        const projectInfo: ProjectInfo = {
          id: data.project?.id ?? '',
          slug: data.project?.slug ?? repoSlug ?? '',
          created: data.project?.created ?? false,
        };
        setProject(projectInfo);
        setState('accessible');
        setWaitingForReturn(false);
        queryClient.invalidateQueries({ queryKey: projectKeys.list() });
      } else if (data.status === 'needs_installation') {
        // Need to install GitHub App
        const returnUri = window.location.href;
        const url = data.install_url ?? buildInstallUrl(repoSlug ?? '', returnUri);
        setInstallUrl(url);
        setState('needs_app');
      } else if (data.status === 'auth_failed') {
        setErrorType('auth_expired');
        setErrorMessage('Your GitHub session has expired. Please sign in again.');
        setState('error');
        setWaitingForReturn(false);
      } else if (data.status === 'no_access') {
        setErrorType('network');
        setErrorMessage("You don't have access to this repository on GitHub.");
        setState('error');
        setWaitingForReturn(false);
      } else {
        setErrorType('network');
        setErrorMessage(data.message ?? 'Could not find this repository.');
        setState('error');
        setWaitingForReturn(false);
      }
    },
    onError: (error: { type?: ErrorType; message?: string }) => {
      setErrorType(error.type ?? 'network');
      setErrorMessage(error.message ?? 'An error occurred. Please try again.');
      setState('error');
      setWaitingForReturn(false);
    },
  });

  // Handle URL input submission
  const handleSubmit = useCallback(
    (e?: React.FormEvent) => {
      e?.preventDefault();

      const parsed = parseRepoUrl(urlInput);
      if (!parsed) {
        setErrorType('invalid_url');
        setErrorMessage('Invalid GitHub repository URL');
        setState('error');
        return;
      }

      setRepoSlug(parsed);
      setErrorMessage(null);
      setErrorType(null);
      setState('checking');
      checkAccess.mutate(parsed);
    },
    [urlInput, checkAccess]
  );

  // Initial check for restore mode
  useEffect(() => {
    if (mode === 'restore' && initialRepoSlug && !initialCheckDone.current) {
      initialCheckDone.current = true;
      checkAccess.mutate(initialRepoSlug);
    }
  }, [mode, initialRepoSlug, checkAccess]);

  // Recheck access (called on visibility change)
  const recheckAccess = useCallback(() => {
    if (repoSlug && state === 'needs_app') {
      setState('checking');
      checkAccess.mutate(repoSlug);
    }
  }, [repoSlug, state, checkAccess]);

  // Visibility change handler for auto-recheck
  useEffect(() => {
    if (!waitingForReturn) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        recheckAccess();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [waitingForReturn, recheckAccess]);

  // Story 585: reload the repo list when the user returns from the App install page
  useEffect(() => {
    if (!waitingForInstall) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setWaitingForInstall(false);
        refetchRepos();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [waitingForInstall, refetchRepos]);

  // Story 585: open the App install page (also used to choose more repos)
  const handleInstallClick = useCallback(() => {
    const installUrl = repoList.data?.install_url;
    if (!installUrl) return;
    setWaitingForInstall(true);
    window.open(installUrl, '_blank');
  }, [repoList.data?.install_url]);

  // Story 585: picking a listed repo runs the same access check as an entered URL
  const handlePick = useCallback(
    (fullName: string) => {
      setRepoSlug(fullName);
      setErrorMessage(null);
      setErrorType(null);
      setWaitingForInstall(false);
      setState('checking');
      checkAccess.mutate(fullName);
    },
    [checkAccess]
  );

  // Handle clicking the GitHub link
  const handleGrantAccessClick = useCallback(() => {
    setWaitingForReturn(true);
    if (installUrl) {
      window.open(installUrl, '_blank');
    }
  }, [installUrl]);

  // Handle retry
  const handleRetry = useCallback(() => {
    setErrorMessage(null);
    setErrorType(null);
    if (mode === 'restore' && repoSlug) {
      setState('checking');
      checkAccess.mutate(repoSlug);
    } else {
      setState('idle');
    }
  }, [mode, repoSlug, checkAccess]);

  // Handle going to project
  const handleGoToProject = useCallback(() => {
    if (project) {
      onSuccess(project);
    }
  }, [project, onSuccess]);

  // Determine title based on mode
  const title = mode === 'add' ? 'Add a repository' : 'Restore access';

  // Story 585: first load, or a retry after the list failed
  const listLoading = repoList.isPending || (repoList.isError && repoList.isFetching);

  return (
    <div className="repo-resolver">
      <div className="repo-resolver__header">
        <h3 className="repo-resolver__title">{title}</h3>
        {onClose && (
          <button
            className="repo-resolver__close"
            onClick={onClose}
            type="button"
            aria-label="Close"
            data-testid="close-button"
          >
            <svg viewBox="0 0 16 16" fill="currentColor">
              <path d="M3.72 3.72a.75.75 0 0 1 1.06 0L8 6.94l3.22-3.22a.749.749 0 0 1 1.275.326.749.749 0 0 1-.215.734L9.06 8l3.22 3.22a.749.749 0 0 1-.326 1.275.749.749 0 0 1-.734-.215L8 9.06l-3.22 3.22a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042L6.94 8 3.72 4.78a.75.75 0 0 1 0-1.06Z" />
            </svg>
          </button>
        )}
      </div>

      <div className="repo-resolver__content">
        {/* Story 585: repo list - add mode, list entry, before a repo is picked */}
        {mode === 'add' && entry === 'list' && state === 'idle' && (
          <div className="repo-resolver__list-area">
            {listLoading && (
              <div className="repo-resolver__checking" data-testid="repo-list-loading">
                <span className="repo-resolver__spinner" />
                Loading your repositories...
              </div>
            )}

            {!listLoading && repoList.isError && (
              <div className="repo-resolver__error" data-testid="repo-list-error">
                {repoList.error.type === 'auth_expired'
                  ? 'Your GitHub session has expired. Please sign in again.'
                  : "Couldn't load your repositories."}
                {repoList.error.type !== 'auth_expired' && (
                  <button
                    type="button"
                    className="repo-resolver__retry-link"
                    onClick={() => refetchRepos()}
                  >
                    Try again
                  </button>
                )}
              </div>
            )}

            {!listLoading && repoList.isSuccess && repoList.data.repos.length === 0 && (
              <div className="repo-resolver__install-step" data-testid="install-step">
                <p className="repo-resolver__install-message">
                  Ceetrix can't see any of your GitHub repositories yet. Install Ceetrix on
                  GitHub and choose which repositories it may use.
                </p>
                <button
                  type="button"
                  className="repo-resolver__grant-button"
                  onClick={handleInstallClick}
                  data-testid="install-app-button"
                >
                  Install Ceetrix on GitHub
                  <svg className="repo-resolver__external-icon" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M3.75 2h3.5a.75.75 0 0 1 0 1.5h-3.5a.25.25 0 0 0-.25.25v8.5c0 .138.112.25.25.25h8.5a.25.25 0 0 0 .25-.25v-3.5a.75.75 0 0 1 1.5 0v3.5A1.75 1.75 0 0 1 12.25 14h-8.5A1.75 1.75 0 0 1 2 12.25v-8.5C2 2.784 2.784 2 3.75 2Zm6.854-1h4.146a.25.25 0 0 1 .25.25v4.146a.25.25 0 0 1-.427.177L13.03 4.03 9.28 7.78a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042l3.75-3.75-1.543-1.543A.25.25 0 0 1 10.604 1Z" />
                  </svg>
                </button>
              </div>
            )}

            {!listLoading && repoList.isSuccess && repoList.data.repos.length > 0 && (
              <>
                <RepoPicker repos={repoList.data.repos} onPick={handlePick} />
                <button
                  type="button"
                  className="repo-resolver__retry-link"
                  onClick={handleInstallClick}
                  data-testid="choose-more-link"
                >
                  Don't see your repository? Choose more on GitHub
                </button>
              </>
            )}

            {waitingForInstall && (
              <div className="repo-resolver__waiting" data-testid="install-waiting">
                <span className="repo-resolver__spinner" />
                Waiting for you to return...
              </div>
            )}

            {!listLoading && (
              <button
                type="button"
                className="repo-resolver__retry-link"
                onClick={() => setEntry('address')}
                data-testid="show-address-entry"
              >
                Or enter a repository address
              </button>
            )}
          </div>
        )}

        {/* Story 585: a picked repo failed its access check */}
        {mode === 'add' && entry === 'list' && state === 'error' && (
          <div className="repo-resolver__error" data-testid="error-state">
            {errorMessage}
            <button
              type="button"
              className="repo-resolver__retry-link"
              onClick={handleRetry}
            >
              Try again
            </button>
          </div>
        )}

        {/* URL Input - add mode, address entry, idle/error states */}
        {mode === 'add' && entry === 'address' && (state === 'idle' || state === 'error') && (
          <form onSubmit={handleSubmit} className="repo-resolver__form">
            <label htmlFor="repo-url" className="repo-resolver__label">
              Repository URL
            </label>
            <input
              id="repo-url"
              type="text"
              className={`repo-resolver__input ${errorType === 'invalid_url' ? 'repo-resolver__input--error' : ''}`}
              placeholder="github.com/owner/repo or owner/repo"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              autoFocus
              data-testid="repo-url-input"
            />

            {/* Upfront guidance - only in idle state */}
            {state === 'idle' && (
              <div className="repo-resolver__guidance" data-testid="upfront-guidance">
                <svg className="repo-resolver__guidance-icon" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M8 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13ZM0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8Zm6.5-.25A.75.75 0 0 1 7.25 7h1a.75.75 0 0 1 .75.75v2.75h.25a.75.75 0 0 1 0 1.5h-2a.75.75 0 0 1 0-1.5h.25v-2h-.25a.75.75 0 0 1-.75-.75ZM8 6a1 1 0 1 1 0-2 1 1 0 0 1 0 2Z" />
                </svg>
                <div className="repo-resolver__guidance-text">
                  <strong>What happens next:</strong>
                  <ul>
                    <li>If Ceetrix can access this repo, you're done</li>
                    <li>If not, we'll guide you to grant access</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Error message */}
            {state === 'error' && errorMessage && (
              <div className="repo-resolver__error" data-testid="error-state">
                {errorMessage}
                {errorType !== 'invalid_url' && (
                  <button
                    type="button"
                    className="repo-resolver__retry-link"
                    onClick={handleRetry}
                  >
                    Try again
                  </button>
                )}
              </div>
            )}

            <button
              type="submit"
              className="repo-resolver__submit"
              disabled={!urlInput.trim()}
              data-testid="check-access"
            >
              Check access
            </button>
          </form>
        )}

        {/* Checking state */}
        {state === 'checking' && (
          <div className="repo-resolver__status" data-testid="checking-state">
            <div className="repo-resolver__repo-slug">{repoSlug}</div>
            <div className="repo-resolver__checking">
              <span className="repo-resolver__spinner" />
              Checking access...
            </div>
          </div>
        )}

        {/* Accessible state */}
        {state === 'accessible' && (
          <div className="repo-resolver__status" data-testid="accessible-state">
            <div className="repo-resolver__repo-row">
              <span className="repo-resolver__repo-slug">{repoSlug}</span>
              <span className="repo-resolver__checkmark" aria-label="Accessible">
                <svg viewBox="0 0 16 16" fill="currentColor">
                  <path d="M13.78 4.22a.75.75 0 0 1 0 1.06l-7.25 7.25a.75.75 0 0 1-1.06 0L2.22 9.28a.751.751 0 0 1 .018-1.042.751.751 0 0 1 1.042-.018L6 10.94l6.72-6.72a.75.75 0 0 1 1.06 0Z" />
                </svg>
              </span>
            </div>
            <div className="repo-resolver__success-message">
              Access confirmed. {project?.created ? 'Project added.' : 'Project ready.'}
            </div>
            <button
              type="button"
              className="repo-resolver__go-button"
              onClick={handleGoToProject}
              data-testid="go-to-project"
            >
              Go to project
            </button>
          </div>
        )}

        {/* Needs app state */}
        {state === 'needs_app' && (
          <div className="repo-resolver__status" data-testid="needs-app-state">
            <div className="repo-resolver__repo-row">
              <span className="repo-resolver__repo-slug">{repoSlug}</span>
              <span className="repo-resolver__cross" aria-label="Needs access">
                <svg viewBox="0 0 16 16" fill="currentColor">
                  <path d="M3.72 3.72a.75.75 0 0 1 1.06 0L8 6.94l3.22-3.22a.749.749 0 0 1 1.275.326.749.749 0 0 1-.215.734L9.06 8l3.22 3.22a.749.749 0 0 1-.326 1.275.749.749 0 0 1-.734-.215L8 9.06l-3.22 3.22a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042L6.94 8 3.72 4.78a.75.75 0 0 1 0-1.06Z" />
                </svg>
              </span>
            </div>

            <div className="repo-resolver__needs-app-message">
              Ceetrix doesn't have access to this repository yet.
            </div>

            <div className="repo-resolver__instructions">
              <ol>
                <li>Click below to open GitHub settings</li>
                <li>Add this repo to Ceetrix's access list</li>
                <li>Return here — we'll detect it automatically</li>
              </ol>
            </div>

            <button
              type="button"
              className="repo-resolver__grant-button"
              onClick={handleGrantAccessClick}
              data-testid="grant-access-link"
            >
              Grant access on GitHub
              <svg className="repo-resolver__external-icon" viewBox="0 0 16 16" fill="currentColor">
                <path d="M3.75 2h3.5a.75.75 0 0 1 0 1.5h-3.5a.25.25 0 0 0-.25.25v8.5c0 .138.112.25.25.25h8.5a.25.25 0 0 0 .25-.25v-3.5a.75.75 0 0 1 1.5 0v3.5A1.75 1.75 0 0 1 12.25 14h-8.5A1.75 1.75 0 0 1 2 12.25v-8.5C2 2.784 2.784 2 3.75 2Zm6.854-1h4.146a.25.25 0 0 1 .25.25v4.146a.25.25 0 0 1-.427.177L13.03 4.03 9.28 7.78a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042l3.75-3.75-1.543-1.543A.25.25 0 0 1 10.604 1Z" />
              </svg>
            </button>

            {waitingForReturn && (
              <div className="repo-resolver__waiting" data-testid="waiting-indicator">
                <span className="repo-resolver__spinner" />
                Waiting for you to return...
              </div>
            )}
          </div>
        )}

        {/* Error state for restore mode */}
        {mode === 'restore' && state === 'error' && (
          <div className="repo-resolver__status" data-testid="error-state">
            <div className="repo-resolver__repo-slug">{repoSlug}</div>
            <div className="repo-resolver__error">
              {errorMessage}
              <button
                type="button"
                className="repo-resolver__retry-link"
                onClick={handleRetry}
              >
                Try again
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
